<template>
  <b-card no-body>
    <template v-slot:header>
      <h2 class="mb-0">
        <template v-if="innerOrder.id">Редактирование заказа # {{ innerOrder.id }}</template>
        <template v-else>Новый заказ</template>
      </h2>
    </template>

    <div class="block" v-if="Object.keys($page.props.errors).length > 0">
      <b-alert class="mb-0" variant="danger" :show="Object.keys($page.props.errors).length > 0">
        <h4 class="alert-heading">Исправьте ошибки и попробуйте сохранить снова</h4>
        <ul class="list-unstyled mb-0">
          <template v-for="(errors, name) in $page.props.errors">
            <li v-for="(error, i) in errors" :key="name+i">
              {{ error }}
            </li>
          </template>
        </ul>
      </b-alert>
    </div>

    <b-form @submit.prevent="onSubmit">
      <div class="block bg-galsen">
        <h3>Статус и документы</h3>
        <b-form-row>
          <div class="col">
            <b-form-group label="Статус">
              <b-form-select v-model="innerOrder.status">
                <option value="3">Отменён</option>
                <option value="4">В обработке</option>
                <option value="5">В производстве</option>
                <option value="6">Частично отгружен</option>
                <option value="7">Отгружен</option>
                <option value="8">Пусконаладка</option>
                <option value="9">Завершён</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Ссылка на папку с документами" :invalid-feedback="fieldError('docs_dir_link')">
              <b-input v-model="innerOrder.docs_dir_link"
                       :state="fieldState('docs_dir_link')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>

      </div>
      <div class="block stages">
        <h3>Этапы</h3>
        <draggable v-model="innerOrder.stages" handle=".handle" ghost-class="ghost" @change="onStageOrderChanged()">
          <b-input-group v-for="(stage, index) in innerOrder.stages" :key="`stage-${index}`" class="mb-2">
            <b-input-group-prepend is-text>
              <input type="checkbox" v-model="stage.completed">
            </b-input-group-prepend>
            <b-input-group-prepend is-text>
              <font-awesome-icon class="handle" icon="arrows-alt-v"></font-awesome-icon>
            </b-input-group-prepend>
            <b-input-group-prepend is-text>
              <div class="stage-title">{{ stage.title }}</div>
            </b-input-group-prepend>
            <template v-if="stage.type === 'date'">
              <b-input placeholder="Дата" :disabled="stage.completed" v-model.trim="stage.value"></b-input>
            </template>
            <b-input placeholder="Комментарий" :disabled="stage.completed" v-model.trim="stage.comment"
                     @update="($event && $event.length && $event.length > 0) && (! stage.value || (stage.value.length && stage.value.length === 0)) ? stage.value = new Date().toLocaleDateString() : ''"></b-input>
            <b-input-group-append>
              <b-btn variant="outline-secondary" :disabled="stage.completed" @click="onStageDelete(index)">
                <font-awesome-icon icon="times"></font-awesome-icon>
              </b-btn>
            </b-input-group-append>
          </b-input-group>
        </draggable>
      </div>

      <div class="block bg-galsen">
        <h3>Компания и контакт</h3>
        <b-form-row>
          <div class="col-9">
            <b-form-group label="Компания" :invalid-feedback="fieldError('company.name')"
                          :state="fieldState('company.name')">
              <suggestions v-model="innerOrder.company.name"
                           :options="{inputClass: 'form-control autocomplete', placeholder: 'Поиск по названию компании'}"
                           :onItemSelected="onCompanySelected"
                           :onInputChange="onCompanySearch" ref="companyNameEl">
                <div slot="item" slot-scope="props" class="single-item">
                  <div class="name"><b>{{ props.item.name }}</b></div>
                  <div class="name">{{ props.item.city }}</div>
                </div>
              </suggestions>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Город" :invalid-feedback="fieldError('company.city')">
              <b-input v-model="innerOrder.company.city" :disabled="isCompanyEditingDisabled('city')"
                       :state="fieldState('company.city')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>

        <b-form-row>
          <div class="col">
            <b-form-group label="Ф. И. О." :invalid-feedback="fieldError('person.name')" :state="fieldState('person.name')">
              <b-input v-model="innerOrder.person.name" :state="fieldState('person.name')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Должность" :invalid-feedback="fieldError('person.position')">
              <b-input v-model="innerOrder.person.position" :state="fieldState('person.position')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Телефон" :invalid-feedback="fieldError('person.phone')">
              <b-input v-model="innerOrder.person.phone" :state="fieldState('person.phone')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Email" :invalid-feedback="fieldError('person.email')">
              <b-input v-model="innerOrder.person.email" :state="fieldState('person.email')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>
      </div>

      <div class="block">
        <h3>Состав</h3>
        <b-form-row>
          <div class="col-4">
            <b-form-group label="Код">
              <suggestions v-model="setQuery"
                           :options="{inputClass: 'form-control autocomplete', placeholder: 'Поиск по коду комплекта'}"
                           :onItemSelected="onSetSelected"
                           :onInputChange="onSetSearch">
                <div slot="item" slot-scope="props" class="single-item">
                  <div class="name"><b>{{ props.item.code }}</b></div>
                  <div class="name">{{ props.item.price | currency }}&nbsp;₽</div>
                  <div class="name">{{ props.item.section }}</div>
                </div>
              </suggestions>
            </b-form-group>
          </div>

          <div class="col-4">
            <b-form-group label="Наименование">
              <b-input v-model="set.section" :disabled="isSetNameDisabled()"></b-input>
            </b-form-group>
          </div>

          <div class="col-1">
            <b-form-group label="Кол-во">
              <b-input v-model="set.quantity" type="number" min="1"></b-input>
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group label="Цена/шт">
              <b-input v-model.lazy="set.price" v-money="moneyConfig" ref="price"></b-input>
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group>
              <label>&nbsp;</label>
              <b-btn block variant="outline-primary" @click="addPositionToOrder" :disabled="isAddSetBtnDisabled()">
                Добавить
              </b-btn>
            </b-form-group>
          </div>
        </b-form-row>

        <set-list :sets.sync="innerOrder.sets" :custom-items.sync="innerOrder.custom_items"></set-list>
      </div>

      <div class="block bg-galsen">

        <h3>Прочее</h3>
        <b-tabs content-class="mt-3">
          <b-tab title="Договор и условия">
            <b-form-row>
              <div class="col">
                <b-form-group label="Номер договора" :invalid-feedback="fieldError('contract_num')"
                              :state="fieldState('contract_num')">
                  <b-input v-model="innerOrder.contract_num" :state="fieldState('contract_num')"></b-input>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Дата заключения договора" :invalid-feedback="fieldError('contract_date')"
                              :state="fieldState('contract_date')">
                  <b-input v-model="innerOrder.contract_date" :state="fieldState('contract_date')"></b-input>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Срок поставки" :invalid-feedback="fieldError('deadline_date')"
                              :state="fieldState('deadline_date')">
                  <b-input v-model="innerOrder.deadline_date" :state="fieldState('deadline_date')"></b-input>
                </b-form-group>
              </div>
            </b-form-row>
            <b-form-row>
              <div class="col">
                <b-form-group label="Адрес доставки" :invalid-feedback="fieldError('delivery_address')"
                              :state="fieldState('delivery_address')">
                  <b-input v-model.trim="innerOrder.delivery_address" :state="fieldState('delivery_address')"></b-input>
                </b-form-group>
              </div>
<!--              <div class="col">-->
<!--                <b-form-group label="Трекинг код" :invalid-feedback="fieldError('tracking_code')"-->
<!--                              :state="fieldState('tracking_code')">-->
<!--                  <b-input v-model.trim="innerOrder.tracking_code" :state="fieldState('tracking_code')"></b-input>-->
<!--                </b-form-group>-->
<!--              </div>-->
            </b-form-row>

            <b-form-group>
              <b-check v-model="innerOrder.install" :state="fieldState('install')">
                Установка
                <b-form-invalid-feedback :state="fieldState('install')">
                  {{ fieldError('install') }}
                </b-form-invalid-feedback>
              </b-check>
            </b-form-group>
          </b-tab>

          <b-tab title="Комментарии">
            <comments :comments="innerOrder.comments"></comments>

            <b-form-group label="Добавить комментарий" :invalid-feedback="fieldError('comment')"
                          :state="fieldState('comment')">
              <b-textarea v-model="innerOrder.comment"
                          :state="fieldState('comment')" :trim="true" max-rows="20"></b-textarea>
            </b-form-group>
          </b-tab>
        </b-tabs>

        <div class="text-right mt-3">
          <b-btn :disabled="! order.can.delete" @click="onDelete()" v-if="innerOrder.id"
                 variant="outline-light" class="text-danger mr-3">Удалить</b-btn>
          <b-btn-group>
            <b-btn :disabled="innerOrder.id ? ! order.can.update : ! $page.props.can.orders['create']"
                   @click="onSubmit($event)"
                   size="lg" variant="outline-primary">Применить</b-btn>
            <b-btn :disabled="innerOrder.id ? ! order.can.update : ! $page.props.can.orders['create']"
                   @click="onSubmit($event, true)"
                   size="lg" variant="outline-primary">Сохранить и закрыть</b-btn>
          </b-btn-group>
        </div>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import draggable from 'vuedraggable'
import 'v-suggestions/dist/v-suggestions.css'
// import MaskedInput from 'vue-masked-input'
import Suggestions from 'v-suggestions'
import SetList from '~/Components/Set/SetList'
import MoneyConfig from '~/Components/MoneyConfig'
import Comments from '~/Components/Comment/Comments'

export default {
  components: {
    Comments,
    // MaskedInput,
    Suggestions,
    SetList,
    draggable
  },
  props: {
    invoice: Object,
    order: Object,
    sets: Array,
    companies: Array,
    stages: Array
  },
  data () {
    return {
      innerOrder: {
        id: null,
        status: 4,
        company: {
          id: null,
          name: '',
          city: null
        },
        person: {
          id: null,
          name: '',
          position: null,
          phone: null,
          email: null
        },
        contract_date: null,
        contract_num: null,
        deadline_date: null,
        delivery_address: null,
        tracking_code: null,
        install: false,
        sets: [],
        custom_items: [],
        comments: [],
        comment: null,
        stages: []
      },
      moneyConfig: MoneyConfig,
      setQuery: '',
      set: {
        id: null,
        name: null,
        code: null,
        price: 0,
        quantity: 1
      }
    }
  },
  created () {
    if (this.order) {
      this.innerOrder = { ...this.order }
    }

    if (!this.order) {
      // set default stages
      this.innerOrder.stages = this.stages.map(stage => ({
        id: stage.id,
        title: stage.title,
        type: stage.type,
        order: stage.order,
        completed: false,
        comment: null
      }))
    }

    // fill order data from invoice
    if (this.invoice) {
      this.innerOrder.proforma_invoice_id = this.invoice.id
      this.innerOrder.company = { ...this.invoice.company }
      this.innerOrder.person = { ...this.invoice.person }
      this.innerOrder.install = this.invoice.install
      this.innerOrder.sets = this.invoice.sets
      this.innerOrder.custom_items = this.invoice.custom_items
    }
  },

  beforeUpdate () {
    // workaround for v-suggestions
    this.$refs.companyNameEl.$el.children[0].classList.remove('is-invalid')

    if (this.fieldError('company.name')) {
      this.$refs.companyNameEl.$el.children[0].classList.add('is-invalid')
    }
  },
  watch: {
    // 'innerOrder.stages': function (val) {
    //   console.log(val)
    // }
  },
  methods: {
    onSubmit (event, redirectToList = false) {
      if (Number.isInteger(this.innerOrder.id)) {
        const route = this.route('backend.orders.update', {
          order: this.innerOrder.id,
          ...(redirectToList && { redirectToList: true })
        })

        this.$inertia.put(route, this.innerOrder)
        return
      }

      const route = this.route('backend.orders.store', { ...(redirectToList && { redirectToList: true }) })
      this.$inertia.post(route, this.innerOrder)
    },

    onDelete () {
      if (!confirm('Вы уверены?')) {
        return
      }

      this.$inertia.delete(this.route('backend.orders.destroy', this.innerOrder.id))
    },

    isCompanyEditingDisabled (field) {
      const company = this.companies.find(company => this.innerOrder.company.id === company.id)

      return typeof company === 'object' && !(company[field] === '' || company[field] === null)
    },

    isSetNameDisabled () {
      return this.setQuery === this.set.code
    },

    isAddSetBtnDisabled () {
      return (!this.set.code && this.setQuery === '') ||
          this.set.quantity === '' ||
          this.set.price === ''
    },

    /**
     * Обработчик выбора в списке подсказок автокомплита компаний
     */
    onCompanySelected (item) {
      this.innerOrder.company = { ...item }
      this.innerOrder.person = { ...item.person }
    },

    /**
     * Обработчик инпута автокомплита компаний
     */
    onCompanySearch (companyQuery) {
      if (companyQuery.trim().length === 0) {
        this.innerOrder.company = {
          id: null,
          name: null,
          city: null
        }
        return
      }

      const result = []

      this.companies.forEach((company) => {
        if (
          (typeof company.name === 'string' && company.name.toLowerCase().includes(companyQuery.toLowerCase())) ||
            (typeof company.city === 'string' && company.city.toLowerCase().includes(companyQuery.toLowerCase()))
        ) {
          result.push(company)
        }
      })

      return result
    },

    /**
     * Обработчик выбора в списке подсказок автокомплита комплектов
     */
    onSetSelected (item) {
      this.setQuery = item.code
      this.$refs.price.$el.value = item.price // workaround для v-money v-bootstrap
      this.set = {
        id: item.id ?? null,
        section: item.section,
        code: item.code ?? null,
        price: item.price ?? 0,
        quantity: 1
      }
      // this.set = { ...item }

      this.set.quantity = this.set.quantity || 1
    },

    /**
     * Обработчик инпута автокомплита комплектов
     */
    onSetSearch (setQuery) {
      if (setQuery.trim().length === 0) {
        return null
      }

      const result = []

      this.sets.forEach((item) => {
        if (
          (typeof item.code === 'string' && item.code.toLowerCase().includes(setQuery.toLowerCase())) ||
            (typeof item.section === 'string' && item.section.toLowerCase().includes(setQuery.toLowerCase()))
        ) {
          result.push(item)
        }
      })

      return result
    },

    /**
     * Добавление позиции в состав инвойса
     */
    addPositionToOrder () {
      if (!this.innerOrder.sets) {
        this.innerOrder.sets = []
      }

      if (!this.innerOrder.custom_items) {
        this.innerOrder.custom_items = []
      }

      if (!this.set.code || this.set.code !== this.setQuery) {
        // Добавление кастома
        this.set.id = null
        this.set.code = this.setQuery
        this.innerOrder.custom_items.push(this.set)
      } else {
        // Добавление стандартного комплекта
        this.innerOrder.sets.push(this.set)
      }

      // Очищаем форму и прочее
      this.setQuery = ''

      this.$nextTick(() => {
        this.set = {
          id: null,
          name: null,
          code: null,
          price: 0,
          quantity: 1
        }
      })
    },

    onStageDelete (index) {
      if (confirm('Удалить?')) {
        this.innerOrder.stages.splice(index, 1)
      }
    },

    onStageOrderChanged () {
      this.innerOrder.stages = [...this.innerOrder.stages].map((item, index) => {
        item.order = index + 1
        return item
      })
    }
  }
}
</script>

<style scoped lang="scss">
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}

.v-suggestions .suggestions {
  max-height: 200px;
  overflow-y: scroll;
}

.block {
  padding: 20px;
}

.stages {
  //.list-group {
  //  min-height: 20px;
  //}
  //.list-group-item {
  //  cursor: pointer;
  //}
  //.flip-list-move {
  //  transition: transform 0.5s;
  //}
  //.no-move {
  //  transition: transform 0s;
  //}
  .stage-title {
    width: 250px;
    text-align: left;
  }

  .ghost {
    opacity: 0.6;
    background: white;
  }

  .handle {
    cursor: move;
  }
}
</style>
